import { useState, useEffect, useMemo } from "react";
import { useAdminLanguage } from "../../layouts/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { ScrollText, User, Clock, Filter, Trash2, Download, ChevronUp, ChevronDown, ArrowUpDown, Search } from "lucide-react";
import { toast } from "sonner";
import { exportToCSV } from "../../utils/exportCSV";

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_LABELS = {
  create_job: { label: "Job Created", color: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  update_job: { label: "Job Updated", color: "bg-sky-500/10 text-sky-400 border-sky-500/20" },
  delete_job: { label: "Job Deleted", color: "bg-red-500/10 text-red-400 border-red-500/20" },
  create_link: { label: "Link Created", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  add_candidate: { label: "Candidate Added", color: "bg-violet-500/10 text-violet-400 border-violet-500/20" },
  credit_gift: { label: "Credits Gift", color: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  pricing_update: { label: "Pricing Updated", color: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
  pending: { label: "Credit Request", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  approved: { label: "Credit Approved", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  rejected: { label: "Credit Rejected", color: "bg-red-500/10 text-red-400 border-red-500/20" },
};

function getActionBadge(action) {
  const config = ACTION_LABELS[action] || { label: action || "System", color: "bg-zinc-700/10 text-zinc-400 border-zinc-700/20" };
  return <Badge className={config.color}>{config.label}</Badge>;
}

function getLevelBadge(level) {
  const colors = {
    activity: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    info: "bg-zinc-700/10 text-zinc-400 border-zinc-700/20",
    warning: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    error: "bg-red-500/10 text-red-400 border-red-500/20",
    pending_credit: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  };
  return <Badge className={colors[level] || colors.info}>{level}</Badge>;
}

export default function AdminLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const { t } = useAdminLanguage();

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    try {
      const res = await fetch(`${API}/api/admin/logs`, { credentials: "include" });
      if (res.ok) setLogs(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleClear = async () => {
    if (!window.confirm("Clear all logs? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/api/admin/logs`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Logs cleared"); fetchLogs(); }
    } catch (e) { toast.error("Error"); }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />;
  };

  // Get unique employees for filter
  const employees = useMemo(() => {
    const map = new Map();
    logs.forEach(l => {
      if (l.admin_id && l.admin_name && l.admin_name !== "System") {
        map.set(l.admin_id, { name: l.admin_name, email: l.admin_email });
      }
    });
    return Array.from(map.entries()).map(([id, info]) => ({ id, ...info }));
  }, [logs]);

  // Get unique actions for filter
  const actions = useMemo(() => {
    const set = new Set();
    logs.forEach(l => { if (l.action) set.add(l.action); });
    return Array.from(set).sort();
  }, [logs]);

  const filtered = useMemo(() => {
    let result = [...logs];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(l =>
        (l.message || "").toLowerCase().includes(s) ||
        (l.admin_name || "").toLowerCase().includes(s) ||
        (l.action || "").toLowerCase().includes(s)
      );
    }
    if (filterEmployee !== "all") {
      result = result.filter(l => l.admin_id === filterEmployee);
    }
    if (filterAction !== "all") {
      result = result.filter(l => l.action === filterAction);
    }
    result.sort((a, b) => {
      let va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return result;
  }, [logs, search, filterEmployee, filterAction, sortKey, sortDir]);

  const activityLogs = useMemo(() => filtered.filter(l => l.level === "activity"), [filtered]);
  const totalActivity = activityLogs.length;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">{t("logs")}</h1>
          <p className="text-zinc-500 text-sm">{filtered.length} entries ({totalActivity} employee actions)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white" onClick={() => exportToCSV(filtered, [
            { label: "Date", key: "created_at" },
            { label: "Employee", key: "admin_name" },
            { label: "Action", key: "action" },
            { label: "Message", key: "message" },
            { label: "Level", key: "level" }
          ], "ema_activity_logs")}>
            <Download className="w-4 h-4 mr-2" />CSV
          </Button>
          <Button variant="outline" className="border-red-800/50 text-red-400 hover:bg-red-900/30" onClick={handleClear}>
            <Trash2 className="w-4 h-4 mr-2" />{t("clear_logs")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900 border-zinc-800 mb-4">
        <CardContent className="py-3 px-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-zinc-500" />
              <span className="text-zinc-400 text-sm font-medium">Filters:</span>
            </div>
            <div className="relative flex-1 max-w-xs">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="logs-search"
                placeholder="Search logs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white pl-9 h-9"
              />
            </div>
            <Select value={filterEmployee} onValueChange={setFilterEmployee}>
              <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700 text-white h-9" data-testid="filter-employee">
                <User className="w-3.5 h-3.5 mr-2 text-zinc-500" />
                <SelectValue placeholder="All employees" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all" className="text-white">All employees</SelectItem>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id} className="text-white">{emp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="w-44 bg-zinc-800 border-zinc-700 text-white h-9" data-testid="filter-action">
                <ScrollText className="w-3.5 h-3.5 mr-2 text-zinc-500" />
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all" className="text-white">All actions</SelectItem>
                {actions.map(a => (
                  <SelectItem key={a} value={a} className="text-white">{ACTION_LABELS[a]?.label || a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="text-zinc-400 cursor-pointer select-none w-44" onClick={() => handleSort("created_at")}>
                  <span className="flex items-center"><Clock className="w-3.5 h-3.5 mr-1.5" />Date<SortIcon col="created_at" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none w-40" onClick={() => handleSort("admin_name")}>
                  <span className="flex items-center"><User className="w-3.5 h-3.5 mr-1.5" />Employee<SortIcon col="admin_name" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 w-36">Action</TableHead>
                <TableHead className="text-zinc-400 w-20">Type</TableHead>
                <TableHead className="text-zinc-400">Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((log, i) => (
                <TableRow key={log.log_id || i} className="border-zinc-800 hover:bg-zinc-800/50">
                  <TableCell className="text-zinc-500 text-xs">
                    {log.created_at ? new Date(log.created_at).toLocaleString() : "-"}
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="text-white text-sm font-medium">{log.admin_name || "System"}</span>
                      {log.admin_email && <p className="text-zinc-500 text-xs truncate max-w-[140px]">{log.admin_email}</p>}
                    </div>
                  </TableCell>
                  <TableCell>{getActionBadge(log.action)}</TableCell>
                  <TableCell>{getLevelBadge(log.level)}</TableCell>
                  <TableCell className="text-zinc-300 text-sm max-w-md truncate">{log.message || "-"}</TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-zinc-500 py-12">
                    {loading ? "Loading..." : "No logs found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
